/**
 * Auth0ProviderWithHistory.tsx
 *
 * An Auth0Provider with extra functionality to account for redirecting a user back to the
 * actualy page they tried to log in from, instead of the default one.
 *
 */
import React from "react";
import { useHistory } from "react-router-dom";
import { AppState, Auth0Provider } from "@auth0/auth0-react";

const Auth0ProviderWithHistory = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

  if (!domain || !clientId || !audience) {
    throw new Error(
      "Missing Auth0 env settings\n" +
        `VITE_AUTH0_DOMAIN=${import.meta.env.VITE_AUTH0_DOMAIN}\n` +
        `VITE_AUTH0_CLIENT_ID=${import.meta.env.VITE_AUTH0_CLIENT_ID}\n` +
        `VITE_AUTH0_AUDIENCE=${import.meta.env.VITE_AUTH0_AUDIENCE}`,
    );
  }

  const history = useHistory();

  const onRedirectCallback = (appState?: AppState) => {
    history.push(appState?.returnTo || globalThis.window.location.pathname);
  };

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: globalThis.window.location.origin,
        audience: audience,
        scope: "openid profile email offline_access",
      }}
      onRedirectCallback={onRedirectCallback}
      useRefreshTokens={true}
      // When a cached session has no refresh token for the current
      // audience/scope (e.g. an older session predating a config change),
      // fall back to silent iframe auth (prompt=none) instead of throwing
      // MissingRefreshTokenError. A real failure still surfaces as
      // login_required, which AuthAPIHook already handles.
      useRefreshTokensFallback={true}
      cacheLocation="localstorage"
    >
      {children}
    </Auth0Provider>
  );
};

export default Auth0ProviderWithHistory;
